import React from 'react';
import { Button, Dialog, DialogContent, DialogFooter, Flex, Select, Text, TextField, useToast, TagBadge } from '@chatsdet/ui';
import {
  usePreviewPostmanImport,
  useImportPostmanCollection,
  type PostmanCollectionVariable,
  type VariableOverride,
} from '../api';
import { useOrgPropertySelection } from '../../../hooks/useOrgPropertySelection';

type VariableConfig = {
  key: string;
  defaultValue: string;
  mode: 'hardcode' | 'parameter';
  value: string;
};

type ImportPostmanDialogProps = {
  onClose: () => void;
};

export const ImportPostmanDialog: React.FC<ImportPostmanDialogProps> = ({ onClose }) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [step, setStep] = React.useState<'upload' | 'configure'>('upload');
  const [variables, setVariables] = React.useState<VariableConfig[]>([]);
  const { toast } = useToast();

  const previewMutation = usePreviewPostmanImport();
  const importMutation = useImportPostmanCollection();
  const { propertyId, selectProperty, propertyOptions, propertiesQuery } = useOrgPropertySelection();

  const handleFileChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setStep('upload');
    setVariables([]);
  }, []);

  const handlePreview = React.useCallback(() => {
    if (!file) return;
    previewMutation.mutate(file, {
      onSuccess: (result) => {
        const vars = result.data?.variables ?? [];
        setVariables(
          vars.map((v: PostmanCollectionVariable) => ({
            key: v.key,
            defaultValue: v.value,
            mode: 'hardcode' as const,
            value: v.value,
          }))
        );
        setStep('configure');
      },
      onError: () => {
        // toast handled globally by MutationCache
      },
    });
  }, [file, previewMutation, toast]);

  const updateVariable = React.useCallback(
    (index: number, updates: Partial<VariableConfig>) => {
      setVariables((prev) => prev.map((v, i) => (i === index ? { ...v, ...updates } : v)));
    },
    []
  );

  const handleImport = React.useCallback(() => {
    if (!file || !propertyId) return;

    const overrides: Record<string, VariableOverride> = {};
    for (const v of variables) {
      overrides[v.key] = { value: v.value, mode: v.mode };
    }

    importMutation.mutate(
      { file, propertyId, variableOverrides: overrides },
      {
        onSuccess: (result) => {
          const data = result.data;
          if (data) {
            toast.success(
              `Imported ${data.imported} endpoint${data.imported !== 1 ? 's' : ''}` +
                (data.skipped > 0 ? ` (${data.skipped} skipped)` : '')
            );
          }
          onClose();
        },
        onError: () => {
          // toast handled globally by MutationCache
        },
      }
    );
  }, [file, propertyId, variables, importMutation, toast, onClose]);

  const title = step === 'upload' ? 'Import Postman Collection' : 'Configure Variables';
  const description =
    step === 'upload'
      ? 'Upload a Postman Collection v2.1 JSON file to populate the API catalog.'
      : 'Choose how to handle each variable from the collection.';

  return (
    <Dialog title={title} description={description}>
      <DialogContent className="space-y-4">
        {step === 'upload' ? (
          <>
            <div>
              <Text size="sm" weight="medium" className="mb-1.5 block">
                Collection file
              </Text>
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleFileChange}
                className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20 file:cursor-pointer"
              />
              {file && (
                <Text size="sm" className="text-muted-foreground mt-1">
                  {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </Text>
              )}
            </div>
            <Select
              label="Application"
              items={propertyOptions}
              selectedKey={propertyId}
              onSelectionChange={(key) => selectProperty(key as string)}
              placeholder="Select a application..."
              isDisabled={propertiesQuery.isLoading}
            />
          </>
        ) : (
          <>
            {variables.length === 0 ? (
              <Text size="sm" className="text-muted-foreground">
                No variables found in this collection. You can proceed with the import.
              </Text>
            ) : (
              <div className="divide-y divide-border border-y border-border max-h-[400px] overflow-y-auto">
                {variables.map((v, index) => (
                  <div key={v.key} className="p-3 space-y-2">
                    <Flex align="center" justify="between" gap="sm">
                      <Text size="sm" weight="medium" className="font-mono">
                        {'{{'}
                        {v.key}
                        {'}}'}
                      </Text>
                      <Flex gap="xs">
                        <Button
                          variant={v.mode === 'hardcode' ? 'primary' : 'ghost'}
                          size="sm"
                          onPress={() => updateVariable(index, { mode: 'hardcode' })}
                        >
                          Replace
                        </Button>
                        <Button
                          variant={v.mode === 'parameter' ? 'primary' : 'ghost'}
                          size="sm"
                          onPress={() => updateVariable(index, { mode: 'parameter' })}
                        >
                          Variable
                        </Button>
                      </Flex>
                    </Flex>
                    {v.mode === 'hardcode' ? (
                      <TextField
                        aria-label={`Value for ${v.key}`}
                        value={v.value}
                        onChange={(val) => updateVariable(index, { value: val })}
                        placeholder={v.defaultValue || 'Enter value...'}
                      />
                    ) : (
                      <Flex align="center" gap="xs">
                        <TagBadge name={<>:{v.key}</>} color="gray" hideDot size="sm" />
                        <Text size="sm" className="text-muted-foreground">
                          Will be kept as a parameter placeholder
                        </Text>
                      </Flex>
                    )}
                  </div>
                ))}
              </div>
            )}
            <Text size="sm" className="text-muted-foreground">
              "Replace" substitutes the variable with a fixed value (use for base URL).
              "Variable" keeps it as a parameter placeholder for API sequences.
            </Text>
          </>
        )}
      </DialogContent>
      <DialogFooter>
        {step === 'configure' && (
          <Button variant="ghost" onPress={() => setStep('upload')} className="mr-auto">
            Back
          </Button>
        )}
        <Button variant="secondary" onPress={onClose}>
          Cancel
        </Button>
        {step === 'upload' ? (
          <Button
            variant="primary"
            onPress={handlePreview}
            isDisabled={!file || !propertyId || previewMutation.isPending}
          >
            {previewMutation.isPending ? 'Reading...' : 'Next'}
          </Button>
        ) : (
          <Button
            variant="primary"
            onPress={handleImport}
            isDisabled={!file || !propertyId || importMutation.isPending}
          >
            {importMutation.isPending ? 'Importing...' : 'Import'}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};
